import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { Affix, Collapse, notification, PageHeader, Switch } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import MockImg from 'assets/img/shopping-cart-demo.png';
import { RevxTable } from 'components';
import {
  AiIcon,
  ArrowLeftIcon,
  DocIcon,
  DocxIcon,
  EpsIcon,
  GifIcon,
  IdIcon,
  JpegIcon,
  JpgIcon,
  PdfIcon,
  PngIcon,
  PptIcon,
  PptxIcon,
  PsdIcon,
  RarIcon,
  SvgIcon,
  TiffIcon,
  TifIcon,
  XlsIcon,
  XlsxIcon,
  ZipIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { success, toast } from 'components/RevxMessage';
import { RevxSpin } from 'components/RevxSpin';
import { TableColumnsType } from 'components/RevxTable';
import {
  Headline,
  SectionHeading,
  TextHeavy,
  Title,
} from 'components/RevxTypography';
import { EqptType, Equipment } from 'domains/eqptDomain';
import { MultiTypeEnum, PartialSKU, SKUPart } from 'domains/skuDomain';
import { sortBy } from 'lodash-es';
import { ReactElement, useMemo, useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  UPLOAD_FILE_ADDRESS_PREFIX,
} from 'utils/constants';
import { computeSkuDiffs, DiffInfo } from 'utils/review';
import './SkuPublishReview.less';

type Props = {
  sku: PartialSKU;
  mainSku: PartialSKU;
  eqptTypes: EqptType[];
  exceptEqpts: Equipment[];
  mainExceptEqpts: Equipment[];
  onClose: () => void;
  isMulti: boolean;
  hidePublishBtn: boolean;
  partDiffMap: { [index: string]: { diffs: DiffInfo[]; skuPart: SKUPart } };
};

export const SkuPublishReview = ({
  onClose,
  sku,
  mainSku,
  eqptTypes,
  exceptEqpts,
  mainExceptEqpts,
  isMulti,
  hidePublishBtn,
  partDiffMap,
}: Props) => {
  // Required by computeSkuDiffs, and in the memo's deps, so the diff re-renders on a switch.
  const { unit } = useMeasurementUnit();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const filterObj = useAppSelector((state) => state.sku.filterObj);
  const [publishing, setPublishing] = useState(false);
  const [fileErrorList, setFileErrorList] = useState<string[]>([]);
  const inheritSettingMap: { [index: string]: string } = {
    EXCEPTION_AUTO: t('platform.sku.form.label.exceptionAuto'),
    EXCEPTION_MANUAL: t('platform.sku.form.label.exceptionManual'),
    NO_EXCEPTION: t('platform.sku.form.label.noException'),
  };

  const skuUseMap: { [index: string]: string } = {
    STANDALONE: t('platform.sku.form.label.standaloneSku'),
    PART: t('platform.sku.form.label.includeWithMultiSku'),
    BOTH: t('platform.sku.form.label.both'),
  };

  const fileTypeIcon: { [str: string]: ReactElement } = {
    ai: <AiIcon />,
    doc: <DocIcon />,
    docx: <DocxIcon />,
    eps: <EpsIcon />,
    gif: <GifIcon />,
    indd: <IdIcon />,
    jpeg: <JpegIcon />,
    jpg: <JpgIcon />,
    pdf: <PdfIcon />,
    png: <PngIcon />,
    ppt: <PptIcon />,
    pptx: <PptxIcon />,
    psd: <PsdIcon />,
    rar: <RarIcon />,
    svg: <SvgIcon />,
    tif: <TifIcon />,
    tiff: <TiffIcon />,
    xls: <XlsIcon />,
    xlsx: <XlsxIcon />,
    zip: <ZipIcon />,
  };

  const basicDiffs = useMemo(() => {
    return computeSkuDiffs(
      sku,
      mainSku,
      eqptTypes,
      exceptEqpts,
      mainExceptEqpts,
      isMulti,
      unit
    );
  }, [eqptTypes, exceptEqpts, isMulti, mainExceptEqpts, mainSku, sku, unit]);

  const partDiffs = useMemo(() => {
    const partDiffValues = Object.values(partDiffMap);

    return partDiffValues
      .map((partDiff) => {
        return partDiff.diffs;
      })
      .flat();
  }, [partDiffMap]);

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <div style={{ display: 'flex' }}>
              <Headline key="1" style={{ width: 'auto' }}>
                [Changes Review] {sku.name}
              </Headline>
            </div>
          }
          extra={[
            <RevxButton
              hidden={hidePublishBtn}
              onClick={async () => {
                const errors = [];
                if (
                  sku.active &&
                  !sku?.images?.[0] &&
                  sku.multiType !== MultiTypeEnum.PART
                ) {
                  errors.push(t('platform.sku.galleryImageRequired'));
                }
                if (!sku.skuType) {
                  errors.push(t('platform.sku.skuTypeRequired'));
                }
                if (sku.multiType === MultiTypeEnum.WRAPPER) {
                  if (!sku.parts || sku?.parts?.length === 0) {
                    errors.push(t('platform.sku.skuPartsRequired'));
                  }
                }
                if (errors.length > 0) {
                  notification.open({
                    message: t('common.validationError'),
                    description: (
                      <div>
                        {errors.map((e) => {
                          return (
                            <div
                              key={e}
                              className="ant-form-item-explain-error"
                            >
                              {e}
                            </div>
                          );
                        })}
                      </div>
                    ),
                  });
                  return;
                }

                setPublishing(true);
                try {
                  await skuService.publishSku(sku);
                  success('Sku Published Successfully!');
                  dispatch.sku.setFilter({
                    ...filterObj,
                  });

                  onClose();
                } catch (e) {
                  toast.error(e.originalMessage);
                }
                setPublishing(false);
              }}
              disabled={basicDiffs.length === 0 && partDiffs.length === 0}
              brandGreen
              key="publish"
              type="primary"
            >
              {t('platform.sku.confirmAndPublish')}
            </RevxButton>,
            <RevxButton onClick={onClose} brandGreen key="cancel">
              {t('common.cancel')}
            </RevxButton>,
          ]}
        />
      </Affix>
    </div>
  );

  const columns: TableColumnsType<DiffInfo> = [
    {
      title: '',
      dataIndex: 'title',
      render(val, record) {
        return <SectionHeading>{val}</SectionHeading>;
      },
    },
    {
      title: t('platform.sku.publishReview.column.current'),
      dataIndex: 'main',
      width: '42%',
      render(val: string, record, index) {
        if (record.name === 'active') {
          return <Switch checked={record.main as boolean} />;
        }

        if (record.name === 'galleryImage') {
          if (record.main) {
            return (
              <div
                className="single-sku-gallery-image-pic-wrapper"
                style={{ width: 100, height: 100 }}
              >
                <img
                  className="single-sku-gallery-image-pic"
                  src={UPLOAD_FILE_ADDRESS_PREFIX + record.main}
                  alt="sku"
                  onError={function (e) {
                    const img = e.target as HTMLImageElement;
                    // image?.includes('.pdf')
                    //   ? img.src = `${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${image}`
                    //   : img.src = `${UPLOAD_FILE_ADDRESS_PREFIX}${image}`;
                    img.src = MockImg;
                  }}
                />
              </div>
            );
          }
        }

        if (record.name === 'artFiles') {
          if (record.main) {
            return (
              <RevxFlexLayout>
                {('' + record.main).split(',').map((item) => {
                  const fileExtension = item.split('.').pop() || '';
                  return (
                    <div
                      className="single-sku-gallery-image-pic-wrapper"
                      style={{ width: 100, height: 100 }}
                    >
                      <img
                        hidden={fileErrorList.includes(item)}
                        className="single-sku-gallery-image-pic"
                        src={UPLOAD_FILE_ADDRESS_PREFIX + item}
                        alt="sku"
                        onError={function (e) {
                          // const img = e.target as HTMLImageElement;
                          // image?.includes('.pdf')
                          //   ? img.src = `${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${image}`
                          //   : img.src = `${UPLOAD_FILE_ADDRESS_PREFIX}${image}`;
                          // img.src = MockImg;
                          setFileErrorList([...fileErrorList, item]);
                        }}
                      />
                      <div
                        className="anticon anticon-file anticon-file-sku"
                        hidden={!fileErrorList.includes(item)}
                      >
                        {fileTypeIcon[fileExtension]}
                      </div>
                    </div>
                  );
                })}
              </RevxFlexLayout>
            );
          }
        }

        if (
          record.name === 'qtyRules' ||
          record.name === 'sizes' ||
          record.name === 'paperRules' ||
          record.name === 'presses' ||
          record.name === 'ink' ||
          record.name === 'requiredSvc' ||
          record.name === 'optionalSvc' ||
          record.name === 'extraSpec' ||
          record.name.includes('part')
        ) {
          return {
            children: (
              <ReactDiffViewer
                compareMethod={
                  record.name === 'parts'
                    ? DiffMethod.TRIMMED_LINES
                    : DiffMethod.WORDS_WITH_SPACE
                }
                styles={{
                  variables: {
                    light: {
                      addedBackground: '#F2FCF8',
                      wordAddedBackground: '#B9E2DB',
                      addedGutterBackground: '#B9E2DB',
                      removedBackground: '#FAF9F0',
                      wordRemovedBackground: '#EAE6BC',
                      removedGutterBackground: '#EAE6BC',
                    },
                  },
                }}
                showDiffOnly={false}
                oldValue={record.main as string}
                newValue={record.draft as string}
                splitView={true}
              />
            ),
            props: {
              colSpan: 2,
            },
          };
        }

        let display = val;
        if (record.name === 'skuType') {
          display = String(record.main);
        }

        if (record.name === 'inheritSetting') {
          display = inheritSettingMap[String(record.main)];
        }

        if (record.name === 'multiType') {
          display = skuUseMap[String(record.main)];
        }

        return <TextHeavy delete>{String(display || '')}</TextHeavy>;
      },
    },
    {
      title: t('platform.sku.publishReview.column.updates'),
      dataIndex: 'draft',
      width: '42%',
      render(val: string, record) {
        if (record.name === 'active') {
          return <Switch checked={record.draft as boolean} />;
        }

        if (record.name === 'galleryImage') {
          if (record.draft) {
            return (
              <div
                className="single-sku-gallery-image-pic-wrapper"
                style={{ width: 100, height: 100 }}
              >
                <img
                  className="single-sku-gallery-image-pic"
                  src={UPLOAD_FILE_ADDRESS_PREFIX + record.draft}
                  alt="sku"
                  onError={function (e) {
                    const img = e.target as HTMLImageElement;
                    // image?.includes('.pdf')
                    //   ? img.src = `${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${image}`
                    //   : img.src = `${UPLOAD_FILE_ADDRESS_PREFIX}${image}`;
                    img.src = MockImg;
                  }}
                />
              </div>
            );
          } else {
            return '';
          }
        }

        if (record.name === 'artFiles') {
          if (record.draft) {
            return (
              <RevxFlexLayout>
                {('' + record.draft).split(',').map((item) => {
                  const fileExtension = item.split('.').pop() || '';
                  return (
                    <div
                      className="single-sku-gallery-image-pic-wrapper"
                      style={{ width: 100, height: 100 }}
                    >
                      <img
                        hidden={fileErrorList.includes(item)}
                        className="single-sku-gallery-image-pic"
                        src={UPLOAD_FILE_ADDRESS_PREFIX + item}
                        alt="sku"
                        onError={function (e) {
                          // const img = e.target as HTMLImageElement;
                          // image?.includes('.pdf')
                          //   ? img.src = `${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${image}`
                          //   : img.src = `${UPLOAD_FILE_ADDRESS_PREFIX}${image}`;
                          // img.src = MockImg;
                          setFileErrorList([...fileErrorList, item]);
                        }}
                      />
                      <div
                        className="anticon anticon-file anticon-file-sku"
                        hidden={!fileErrorList.includes(item)}
                      >
                        {fileTypeIcon[fileExtension]}
                      </div>
                    </div>
                  );
                })}
              </RevxFlexLayout>
            );
          }
        }
        if (
          record.name === 'qtyRules' ||
          record.name === 'sizes' ||
          record.name === 'paperRules' ||
          record.name === 'presses' ||
          record.name === 'ink' ||
          record.name === 'requiredSvc' ||
          record.name === 'optionalSvc' ||
          record.name === 'extraSpec' ||
          record.name.includes('part')
        ) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }

        let display = val;
        if (record.name === 'skuType') {
          display = String(record.draft);
        }

        if (record.name === 'inheritSetting') {
          display = inheritSettingMap[String(record.draft)];
        }

        if (record.name === 'multiType') {
          display = skuUseMap[String(record.draft)];
        }
        return <TextHeavy>{String(display)}</TextHeavy>;
      },
    },
  ];

  return (
    <div className="sku-publish">
      <RevxSpin spinning={publishing}>
        {actionBar}

        <div className="sku-publish-review-basic">
          <Collapse defaultActiveKey="0">
            <Collapse.Panel
              header={isMulti ? 'Wrapper SKU Diffs' : 'SKU Diffs'}
              key="0"
            >
              <RevxTable<DiffInfo>
                size="small"
                className="no-sticky"
                columns={columns}
                dataSource={basicDiffs}
                pagination={{ pageSize: 100 }}
              ></RevxTable>
            </Collapse.Panel>
          </Collapse>

          {isMulti && partDiffs.length > 0 && (
            <Collapse style={{ marginTop: 20 }}>
              <Collapse.Panel header="Individual SKU Diffs" key="1">
                {sortBy(Object.keys(partDiffMap), (partId) => {
                  return partDiffMap[partId].skuPart.sequence;
                }).map((partId) => {
                  const partDiff = partDiffMap[partId];
                  return (
                    <div key={partId}>
                      <Title>{partDiff.skuPart.part.name}</Title>
                      <RevxTable<DiffInfo>
                        size="small"
                        className="no-sticky"
                        columns={columns}
                        dataSource={partDiff.diffs}
                        pagination={{ pageSize: 100 }}
                      ></RevxTable>
                    </div>
                  );
                })}
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
      </RevxSpin>
    </div>
  );
};
